import React, { useEffect, useMemo, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { supabase } from './supabase';
import {
  AlertCircle,
  Plus,
  Search,
  Circle,
  X,
  Save,
  Trash2,
  Upload,
  Phone as PhoneIcon,
  Mail,
  Users,
  Ticket,
  Copy as CopyIcon,
  Send,
  RefreshCw,
} from 'lucide-react';
import './CirculoRojo.css';
import CirculoRojoCard from './CirculoRojoCard';

interface Customer {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  phone_display: string | null;
  circulo_rojo: boolean;
  notes: string | null;
  source_origin: string | null;
  first_order_at: string | null;
  last_order_at: string | null;
  order_count: number;
  total_spent: number;
  coupon_code: string | null;
  coupon_created_at: string | null;
  created_at: string;
  updated_at: string;
}

interface Props {
  userEmail: string | undefined;
}

// rafa@ agregado el 26/10/2026 (rol staff, pedido de Martín)
const ALLOWED_EMAILS = ['martin@krilic.com.ar', 'stephanie@krilic.com.ar', 'rafa@krilic.com.ar'];

const fmtARS = (n: number) =>
  n.toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const fmtDate = (iso: string | null) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' });
  } catch {
    return '—';
  }
};

const normalizePhone = (raw: string): string => raw.replace(/[^0-9]/g, '');

interface ParsedRow {
  name: string;
  phone: string | null;
  phone_display: string | null;
  email: string | null;
  notes: string | null;
}

// Parser tolerante: una línea por cliente. Separadores: tab, coma, punto y coma o múltiples espacios.
// Intenta reconocer columnas: nombre, teléfono, email. Si solo hay un teléfono o email, el resto va en nombre.
// Segmento → template de Meta / etiqueta de cola. Agregar acá cada campaña nueva.
function crTemplateFor(origin?: string | null): string {
  if (origin === 'epic_week_2026') return 'circulo_rojo_credencial_nuevo';
  if (origin === 'amigo_week_2026') return 'circulo_rojo_credencial_amigo';
  return 'circulo_rojo_credencial_existente';
}
function crSegmentFor(origin?: string | null): string {
  if (origin === 'epic_week_2026') return 'nuevo';
  if (origin === 'amigo_week_2026') return 'amigo';
  return 'existente';
}

function parseImportText(text: string): ParsedRow[] {
  const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  const emailRe = /[\w.+-]+@[\w-]+\.[\w.-]+/;
  const phoneRe = /(\+?\d[\d\s().-]{6,})/;
  return lines.map(line => {
    let rest = line;
    let email: string | null = null;
    let phoneDisplay: string | null = null;
    const em = rest.match(emailRe);
    if (em) {
      email = em[0];
      rest = rest.replace(em[0], '').trim();
    }
    const ph = rest.match(phoneRe);
    if (ph) {
      const candidate = ph[0].trim();
      if (normalizePhone(candidate).length >= 7) {
        phoneDisplay = candidate;
        rest = rest.replace(ph[0], '').trim();
      }
    }
    // Limpieza de separadores residuales
    const name = rest.replace(/[\t,;|]+/g, ' ').replace(/\s{2,}/g, ' ').trim() || 'Sin nombre';
    return {
      name,
      phone: phoneDisplay ? normalizePhone(phoneDisplay) : null,
      phone_display: phoneDisplay,
      email,
      notes: null,
    };
  });
}

function CirculoRojo({ userEmail }: Props) {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<Customer | null>(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;
  const [generatingCoupons, setGeneratingCoupons] = useState(false);
  const [couponProgress, setCouponProgress] = useState<string | null>(null);
  const [previewCustomerId, setPreviewCustomerId] = useState<string | null>(null);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  type SortField = 'default' | 'order_count' | 'total_spent' | 'last_order_at';
  const [sortField, setSortField] = useState<SortField>('default');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  function toggleSort(field: SortField) {
    if (sortField === field) {
      if (sortDir === 'desc') setSortDir('asc');
      else { setSortField('default'); setSortDir('desc'); }
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  }

  const allowed = !!userEmail && ALLOWED_EMAILS.includes(userEmail);

  useEffect(() => {
    if (allowed) fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed]);

  async function fetchCustomers() {
    setLoading(true);
    try {
      const CHUNK = 1000;
      const all: Customer[] = [];
      let offset = 0;
      while (true) {
        const { data, error } = await supabase
          .from('customers')
          .select('*')
          .order('circulo_rojo', { ascending: false })
          .order('last_order_at', { ascending: false, nullsFirst: false })
          .order('name', { ascending: true })
          .range(offset, offset + CHUNK - 1);
        if (error) throw error;
        const rows = data || [];
        all.push(...(rows as Customer[]));
        if (rows.length < CHUNK) break;
        offset += CHUNK;
        if (offset > 50000) break;
      }
      setCustomers(all);
    } catch (err: any) {
      console.error('fetch customers error', err);
      alert('Error cargando clientes: ' + (err?.message || String(err)));
    } finally {
      setLoading(false);
    }
  }

  const counts = useMemo(() => {
    const total = customers.length;
    const vips = customers.filter(c => c.circulo_rojo).length;
    return { total, vips };
  }, [customers]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = customers.filter(c => {
      if (!showAll && !c.circulo_rojo) return false;
      if (!q) return true;
      const hay = [c.name, c.email || '', c.phone_display || '', c.phone || '', c.notes || ''].join(' ').toLowerCase();
      return hay.includes(q);
    });
    if (sortField === 'default') return rows;
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      let av: number, bv: number;
      if (sortField === 'last_order_at') {
        av = a.last_order_at ? new Date(a.last_order_at).getTime() : 0;
        bv = b.last_order_at ? new Date(b.last_order_at).getTime() : 0;
      } else {
        av = Number(a[sortField] ?? 0);
        bv = Number(b[sortField] ?? 0);
      }
      if (av === bv) return 0;
      return av < bv ? -dir : dir;
    });
  }, [customers, showAll, search, sortField, sortDir]);

  useEffect(() => {
    setPage(0);
  }, [search, showAll, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const paged = useMemo(
    () => filtered.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE),
    [filtered, safePage]
  );

  async function toggleVIP(c: Customer) {
    const { error } = await supabase
      .from('customers')
      .update({ circulo_rojo: !c.circulo_rojo })
      .eq('id', c.id);
    if (error) {
      alert('Error: ' + error.message);
      return;
    }
    setCustomers(prev => prev.map(x => (x.id === c.id ? { ...x, circulo_rojo: !c.circulo_rojo } : x)));
  }

  async function saveCustomer(c: Customer) {
    setSaving(true);
    const payload: any = {
      name: c.name?.trim() || 'Sin nombre',
      email: c.email?.trim() || null,
      phone: c.phone_display ? normalizePhone(c.phone_display) : null,
      phone_display: c.phone_display?.trim() || null,
      notes: c.notes?.trim() || null,
      circulo_rojo: c.circulo_rojo,
    };
    const { error } = await supabase.from('customers').update(payload).eq('id', c.id);
    setSaving(false);
    if (error) {
      alert('Error: ' + error.message);
      return;
    }
    setEditing(null);
    fetchCustomers();
  }

  async function createCustomer(c: Partial<Customer>) {
    setSaving(true);
    const phoneNorm = c.phone_display ? normalizePhone(c.phone_display) : null;
    const payload: any = {
      name: c.name?.trim() || 'Sin nombre',
      email: c.email?.trim() || null,
      phone: phoneNorm || null,
      phone_display: c.phone_display?.trim() || null,
      notes: c.notes?.trim() || null,
      circulo_rojo: c.circulo_rojo ?? true,
      source_origin: 'manual',
    };
    const { error } = await supabase.from('customers').insert(payload);
    setSaving(false);
    if (error) {
      alert('Error: ' + error.message);
      return;
    }
    setShowNewModal(false);
    fetchCustomers();
  }

  async function deleteCustomer(c: Customer) {
    if (!window.confirm(`Eliminar a ${c.name}? Esta acción no se puede deshacer.`)) return;
    const { error } = await supabase.from('customers').delete().eq('id', c.id);
    if (error) {
      alert('Error: ' + error.message);
      return;
    }
    setEditing(null);
    fetchCustomers();
  }

  async function generateMissingCoupons() {
    const missing = customers.filter(c => c.circulo_rojo && !c.coupon_code && c.email);
    if (missing.length === 0) {
      alert('No hay VIPs sin cupón.');
      return;
    }
    if (!window.confirm(`Generar ${missing.length} cupones en Tiendanube? Esto crea los cupones reales (10% off indefinido sin envío).`)) return;
    setGeneratingCoupons(true);
    setCouponProgress(`Comenzando… (${missing.length} pendientes)`);
    try {
      let totalCreated = 0;
      let totalExisting = 0;
      let totalErrors = 0;
      let iter = 0;
      while (true) {
        iter++;
        setCouponProgress(`Batch ${iter} — creados: ${totalCreated} · ya existían: ${totalExisting} · errores: ${totalErrors}`);
        const { data, error } = await supabase.functions.invoke('create-circulo-rojo-coupons', {
          body: { limit: 50 },
        });
        if (error) throw error;
        totalCreated += data?.created || 0;
        totalExisting += data?.already_exists || 0;
        totalErrors += data?.errors || 0;
        if (!data?.more || data?.processed === 0) break;
        if (iter > 50) break;
      }
      alert(`Cupones procesados.\nCreados nuevos: ${totalCreated}\nYa existían en TN: ${totalExisting}\nErrores: ${totalErrors}`);
      fetchCustomers();
    } catch (e: any) {
      alert('Error: ' + (e?.message || String(e)));
    } finally {
      setGeneratingCoupons(false);
      setCouponProgress(null);
    }
  }

  async function importBulk(rows: ParsedRow[]) {
    if (rows.length === 0) return;
    setSaving(true);
    // Insertamos en dos pasos: con phone (upsert por phone) y sin phone.
    const withPhone = rows.filter(r => r.phone);
    const withoutPhone = rows.filter(r => !r.phone);

    let inserted = 0;
    let updated = 0;
    let errors = 0;

    if (withPhone.length > 0) {
      const payload = withPhone.map(r => ({
        name: r.name,
        email: r.email,
        phone: r.phone,
        phone_display: r.phone_display,
        notes: r.notes,
        circulo_rojo: true,
        source_origin: 'manual_import',
      }));
      const { data, error } = await supabase
        .from('customers')
        .upsert(payload, { onConflict: 'phone', ignoreDuplicates: false })
        .select('id');
      if (error) {
        console.error(error);
        errors += withPhone.length;
      } else {
        inserted += data?.length || 0;
      }
    }

    if (withoutPhone.length > 0) {
      const payload = withoutPhone.map(r => ({
        name: r.name,
        email: r.email,
        phone: null,
        phone_display: null,
        notes: r.notes,
        circulo_rojo: true,
        source_origin: 'manual_import',
      }));
      const { error } = await supabase.from('customers').insert(payload);
      if (error) {
        console.error(error);
        errors += withoutPhone.length;
      } else {
        inserted += withoutPhone.length;
      }
    }

    setSaving(false);
    alert(`Importación terminada.\nProcesados: ${rows.length}\nInsertados/actualizados: ${inserted}\nErrores: ${errors}`);
    setShowImportModal(false);
    fetchCustomers();
    return { inserted, updated, errors };
  }

  if (!allowed) {
    return (
      <div className="cr-denied">
        <AlertCircle size={48} />
        <h2>Acceso Denegado</h2>
        <p>Esta sección es exclusiva para la administración.</p>
      </div>
    );
  }

  return (
    <div className="cr-container">
      <div className="cr-header">
        <div className="cr-title">
          <Circle size={22} fill="#dc2626" color="#dc2626" />
          <h1>Círculo Rojo</h1>
          <span className="cr-counter">
            {counts.vips} VIP{counts.vips !== 1 ? 's' : ''} · {counts.total} total
          </span>
        </div>
        <div className="cr-header-actions">
          <button
            className="cr-btn cr-btn-secondary"
            onClick={() => {
              const firstVip = customers.find(c => c.circulo_rojo && c.coupon_code);
              setPreviewCustomerId(firstVip?.id || customers.find(c => c.circulo_rojo)?.id || null);
            }}
            title="Ver mockup de la credencial Círculo Rojo"
          >
            <Circle size={16} fill="#dc2626" color="#dc2626" /> Vista previa credencial
          </button>
          <button
            className="cr-btn cr-btn-secondary"
            onClick={() => setShowCampaignModal(true)}
            title="Cola de envío de credenciales por WhatsApp"
          >
            <Send size={16} /> Campaña WhatsApp
          </button>
          <button
            className="cr-btn cr-btn-secondary"
            onClick={generateMissingCoupons}
            disabled={generatingCoupons}
            title="Crea cupones en Tiendanube para VIPs que no los tienen todavía"
          >
            <Ticket size={16} /> {generatingCoupons ? (couponProgress || 'Generando…') : 'Generar cupones faltantes'}
          </button>
          <button className="cr-btn cr-btn-secondary" onClick={() => setShowImportModal(true)}>
            <Upload size={16} /> Importar lista
          </button>
          <button className="cr-btn cr-btn-primary" onClick={() => setShowNewModal(true)}>
            <Plus size={16} /> Nuevo cliente
          </button>
        </div>
      </div>

      <div className="cr-toolbar">
        <div className="cr-search">
          <Search size={16} />
          <input
            type="text"
            placeholder="Buscar por nombre, teléfono o email…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <label className="cr-toggle">
          <input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} />
          <Users size={16} /> Ver todos los clientes
        </label>
      </div>

      {loading ? (
        <div className="cr-loading">Cargando…</div>
      ) : filtered.length === 0 ? (
        <div className="cr-empty">
          {showAll ? 'No hay clientes que coincidan.' : 'No hay miembros del Círculo Rojo todavía.'}
        </div>
      ) : (
        <div className="cr-table-wrap">
          <table className="cr-table">
            <thead>
              <tr>
                <th style={{ width: 32 }}></th>
                <th>Nombre</th>
                <th>Teléfono</th>
                <th>Email</th>
                <th>Cupón</th>
                <th
                  style={{ textAlign: 'right', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => toggleSort('order_count')}
                >
                  Pedidos {sortField === 'order_count' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th
                  style={{ textAlign: 'right', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => toggleSort('total_spent')}
                >
                  Total {sortField === 'total_spent' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => toggleSort('last_order_at')}
                >
                  Último pedido {sortField === 'last_order_at' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th>Notas</th>
                <th style={{ width: 100 }}></th>
              </tr>
            </thead>
            <tbody>
              {paged.map(c => (
                <tr key={c.id} className={c.circulo_rojo ? 'cr-row-vip' : ''}>
                  <td>
                    <button
                      className="cr-star-btn"
                      title={c.circulo_rojo ? 'Quitar de Círculo Rojo' : 'Agregar a Círculo Rojo'}
                      onClick={() => toggleVIP(c)}
                    >
                      {c.circulo_rojo ? (
                        <Circle size={18} fill="#dc2626" color="#dc2626" />
                      ) : (
                        <Circle size={18} color="#94a3b8" />
                      )}
                    </button>
                  </td>
                  <td className="cr-name">{c.name}</td>
                  <td>
                    {c.phone_display ? (
                      <a href={`https://wa.me/${c.phone || ''}`} target="_blank" rel="noreferrer" className="cr-link">
                        <PhoneIcon size={12} /> {c.phone_display}
                      </a>
                    ) : (
                      <span className="cr-dim">—</span>
                    )}
                  </td>
                  <td>
                    {c.email ? (
                      <a href={`mailto:${c.email}`} className="cr-link">
                        <Mail size={12} /> {c.email}
                      </a>
                    ) : (
                      <span className="cr-dim">—</span>
                    )}
                  </td>
                  <td>
                    {c.coupon_code ? (
                      <button
                        className="cr-coupon-chip"
                        title="Copiar"
                        onClick={() => {
                          navigator.clipboard.writeText(c.coupon_code!);
                        }}
                      >
                        <Ticket size={12} /> {c.coupon_code} <CopyIcon size={10} />
                      </button>
                    ) : (
                      <span className="cr-dim">—</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>{c.order_count}</td>
                  <td style={{ textAlign: 'right' }}>${fmtARS(c.total_spent || 0)}</td>
                  <td>{fmtDate(c.last_order_at)}</td>
                  <td className="cr-notes-cell">{c.notes || ''}</td>
                  <td>
                    <button className="cr-btn-icon" onClick={() => setEditing({ ...c })} title="Editar">
                      Editar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {totalPages > 1 && (
            <div className="cr-pagination">
              <button
                className="cr-btn cr-btn-secondary"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={safePage === 0}
              >
                ‹ Anterior
              </button>
              <span className="cr-pagination-info">
                Página {safePage + 1} de {totalPages} · {filtered.length} resultado{filtered.length !== 1 ? 's' : ''}
              </span>
              <button
                className="cr-btn cr-btn-secondary"
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={safePage >= totalPages - 1}
              >
                Siguiente ›
              </button>
            </div>
          )}
        </div>
      )}

      {editing && (
        <CustomerModal
          customer={editing}
          saving={saving}
          onChange={setEditing}
          onSave={() => saveCustomer(editing)}
          onDelete={() => deleteCustomer(editing)}
          onClose={() => setEditing(null)}
        />
      )}
      {showNewModal && (
        <CustomerModal
          customer={{
            id: '',
            name: '',
            email: null,
            phone: null,
            phone_display: null,
            circulo_rojo: true,
            notes: null,
            source_origin: 'manual',
            first_order_at: null,
            last_order_at: null,
            order_count: 0,
            total_spent: 0,
            coupon_code: null,
            coupon_created_at: null,
            created_at: '',
            updated_at: '',
          }}
          saving={saving}
          isNew
          onChange={() => {}}
          onSave={c => createCustomer(c)}
          onClose={() => setShowNewModal(false)}
        />
      )}
      {showImportModal && (
        <ImportModal
          saving={saving}
          onClose={() => setShowImportModal(false)}
          onImport={importBulk}
        />
      )}
      {previewCustomerId && (
        <CardPreviewModal
          customers={customers.filter(c => c.circulo_rojo)}
          selectedId={previewCustomerId}
          onSelect={setPreviewCustomerId}
          onClose={() => setPreviewCustomerId(null)}
        />
      )}
      {showCampaignModal && (
        <CampaignModal
          customers={customers}
          onClose={() => setShowCampaignModal(false)}
        />
      )}
    </div>
  );
}

interface CampaignModalProps {
  customers: Customer[];
  onClose: () => void;
}

interface CampaignStats {
  pending: number;
  sent: number;
  failed: number;
  skipped: number;
  total: number;
}

function CampaignModal({ customers, onClose }: CampaignModalProps) {
  const [stats, setStats] = useState<CampaignStats | null>(null);
  const [recentErrors, setRecentErrors] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [enqueueing, setEnqueueing] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [enqueueResult, setEnqueueResult] = useState<string | null>(null);

  const eligible = useMemo(() =>
    customers.filter(c => c.circulo_rojo && c.coupon_code && c.phone),
    [customers]);

  async function refreshStats() {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('cr_campaign_queue')
        .select('status');
      if (error) throw error;
      const counts: CampaignStats = { pending: 0, sent: 0, failed: 0, skipped: 0, total: 0 };
      (data || []).forEach((r: any) => {
        counts.total++;
        if (r.status === 'pending') counts.pending++;
        else if (r.status === 'sent') counts.sent++;
        else if (r.status === 'failed') counts.failed++;
        else if (r.status === 'skipped') counts.skipped++;
      });
      setStats(counts);

      const { data: errs } = await supabase
        .from('cr_campaign_queue')
        .select('id, snapshot_name, snapshot_phone, error, fired_at')
        .eq('status', 'failed')
        .order('fired_at', { ascending: false })
        .limit(10);
      setRecentErrors(errs || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refreshStats(); }, []);

  async function handleEnqueueAll() {
    if (eligible.length === 0) { setEnqueueResult('No hay clientes elegibles'); return; }
    if (!window.confirm(`Encolar ${eligible.length} clientes del Círculo Rojo? Se ignoran los que ya están en cola.`)) return;
    setEnqueueing(true);
    setEnqueueResult(null);
    try {
      const rows = eligible.map(c => ({
        customer_id: c.id,
        segment: crSegmentFor(c.source_origin),
        template_name: crTemplateFor(c.source_origin),
        snapshot_name: c.name,
        snapshot_coupon_code: c.coupon_code,
        snapshot_first_order_at: c.first_order_at,
        snapshot_phone: c.phone,
      }));
      // Procesar en chunks de 100 para no pasarse de límites de payload.
      let inserted = 0;
      for (let i = 0; i < rows.length; i += 100) {
        const chunk = rows.slice(i, i + 100);
        const { error } = await supabase
          .from('cr_campaign_queue')
          .upsert(chunk, { onConflict: 'customer_id', ignoreDuplicates: true });
        if (error) throw error;
        inserted += chunk.length;
      }
      setEnqueueResult(`Procesados ${inserted} (los duplicados se ignoraron silenciosamente)`);
      await refreshStats();
    } catch (e: any) {
      setEnqueueResult(`Error: ${e?.message || String(e)}`);
    } finally {
      setEnqueueing(false);
    }
  }

  async function handleResetFailed() {
    if (!window.confirm('Re-encolar todos los fallidos como pending para que el cron los reintente?')) return;
    setResetting(true);
    try {
      const { error } = await supabase
        .from('cr_campaign_queue')
        .update({ status: 'pending', error: null, fired_at: null })
        .eq('status', 'failed');
      if (error) throw error;
      await refreshStats();
    } finally {
      setResetting(false);
    }
  }

  const eligibleByseg = useMemo(() => {
    const nuevo = eligible.filter(c => c.source_origin === 'epic_week_2026').length;
    const existente = eligible.length - nuevo;
    return { existente, nuevo };
  }, [eligible]);

  return (
    <div className="cr-modal-overlay" onClick={onClose}>
      <div className="cr-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 880 }}>
        <div className="cr-modal-header">
          <h2>Campaña WhatsApp · Credencial Círculo Rojo</h2>
          <button className="cr-modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="cr-modal-body">
          {/* Elegibles */}
          <div style={{ marginBottom: 18, padding: 14, border: '1px solid var(--border)', borderRadius: 10 }}>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 6 }}>Clientes elegibles (CR + cupón + phone)</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>
              {eligible.length}{' '}
              <span style={{ fontSize: 13, fontWeight: 400, color: 'var(--text-muted)' }}>
                ({eligibleByseg.existente} históricos · {eligibleByseg.nuevo} Epic Week)
              </span>
            </div>
          </div>

          {/* Stats actuales */}
          <div style={{ marginBottom: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <h3 style={{ margin: 0, fontSize: 15 }}>Estado de la cola</h3>
              <button
                onClick={refreshStats}
                disabled={loading}
                className="cr-btn cr-btn-secondary"
                style={{ padding: '4px 10px', fontSize: 12 }}
              >
                <RefreshCw size={12} style={{ display: 'inline', marginRight: 4 }} /> Refrescar
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10 }}>
              <StatBox label="Total" value={stats?.total ?? '—'} />
              <StatBox label="Pending" value={stats?.pending ?? '—'} color="#3b82f6" />
              <StatBox label="Sent" value={stats?.sent ?? '—'} color="#10b981" />
              <StatBox label="Failed" value={stats?.failed ?? '—'} color="#dc2626" />
              <StatBox label="Skipped" value={stats?.skipped ?? '—'} color="#6b7280" />
            </div>
          </div>

          {/* Acciones */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
            <button onClick={handleEnqueueAll} disabled={enqueueing} className="cr-btn cr-btn-primary">
              {enqueueing ? 'Encolando…' : `Encolar todos (${eligible.length})`}
            </button>
            <button onClick={handleResetFailed} disabled={resetting || (stats?.failed ?? 0) === 0} className="cr-btn cr-btn-secondary">
              {resetting ? 'Reseteando…' : `Reintentar fallidos (${stats?.failed ?? 0})`}
            </button>
            {enqueueResult && (
              <span style={{ fontSize: 12, color: 'var(--text-muted)', alignSelf: 'center' }}>{enqueueResult}</span>
            )}
          </div>

          {/* Info de cron */}
          <div style={{ padding: 12, background: 'var(--bg-secondary, rgba(0,0,0,0.03))', borderRadius: 8, fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6, marginBottom: 14 }}>
            <strong style={{ color: 'var(--text-main)' }}>Cron activo:</strong> cada 15 min entre las 10:00 y 21:00 hs ART, manda 5 mensajes por tick.
            Capacidad: ~220/día → ~3 días para drenar 642.
            Requiere templates APPROVED en Meta (verificar status antes de encolar).
          </div>

          {/* Últimos errores */}
          {recentErrors.length > 0 && (
            <div>
              <h3 style={{ margin: '0 0 8px 0', fontSize: 14 }}>Últimos errores</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 240, overflowY: 'auto' }}>
                {recentErrors.map((e: any) => (
                  <div key={e.id} style={{ padding: 8, border: '1px solid var(--border)', borderRadius: 6, fontSize: 11 }}>
                    <div style={{ fontWeight: 600 }}>{e.snapshot_name} · {e.snapshot_phone}</div>
                    <div style={{ fontFamily: 'monospace', color: '#dc2626', wordBreak: 'break-all', marginTop: 2 }}>{e.error}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatBox({ label, value, color }: { label: string; value: number | string; color?: string }) {
  return (
    <div style={{ padding: 10, border: '1px solid var(--border)', borderRadius: 8, textAlign: 'center' }}>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: color || 'var(--text-main)' }}>{value}</div>
    </div>
  );
}

interface CardPreviewModalProps {
  customers: Customer[];
  selectedId: string;
  onSelect: (id: string) => void;
  onClose: () => void;
}

function CardPreviewModal({ customers, selectedId, onSelect, onClose }: CardPreviewModalProps) {
  const current = customers.find(c => c.id === selectedId) || customers[0];
  const cardRef = useRef<HTMLDivElement>(null);
  const [templateName, setTemplateName] = useState<string>(() => crTemplateFor(current?.source_origin));
  const [testPhone, setTestPhone] = useState('5491169965506');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const [communityLink, setCommunityLink] = useState<string>('');

  useEffect(() => {
    supabase
      .from('cost_settings')
      .select('cr_community_link')
      .eq('id', 1)
      .maybeSingle()
      .then(({ data }) => setCommunityLink(data?.cr_community_link || ''));
  }, []);

  // Re-aplicar template default cuando cambia el cliente.
  useEffect(() => {
    if (!current) return;
    setTemplateName(crTemplateFor(current.source_origin));
    setSendResult(null);
  }, [current?.id, current?.source_origin]);

  async function handleSendTest() {
    if (!current || !cardRef.current) return;
    const phoneDigits = testPhone.replace(/\D/g, '');
    if (phoneDigits.length < 10) {
      setSendResult({ ok: false, msg: 'Phone inválido' });
      return;
    }
    setSending(true);
    setSendResult(null);
    try {
      // Capturar el card a PNG via html2canvas (renderea a escala 1, no la del preview).
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: null,
        scale: 1,
        width: 1080,
        height: 630,
        useCORS: true,
        logging: false,
      });
      const dataUrl = canvas.toDataURL('image/png');
      const imageB64 = dataUrl.split(',')[1];

      const firstName = (current.name || '').trim().split(/\s+/)[0] || 'cliente';
      const { data, error } = await supabase.functions.invoke('send-cr-card-template', {
        body: {
          phone: phoneDigits,
          image_b64: imageB64,
          mime_type: 'image/png',
          template_name: templateName,
          body_params: [firstName, current.coupon_code || '', communityLink],
          contact_name: current.name,
        },
      });
      if (error) throw error;
      if (data?.ok) {
        const wamid = data?.wa_result?.messages?.[0]?.id || '';
        setSendResult({ ok: true, msg: `Enviado ✓ ${wamid.slice(0, 30)}...` });
      } else {
        const errMsg = JSON.stringify(data?.wa_result?.error || data?.error || data, null, 2).slice(0, 400);
        setSendResult({ ok: false, msg: errMsg });
      }
    } catch (e: any) {
      setSendResult({ ok: false, msg: e?.message || String(e) });
    } finally {
      setSending(false);
    }
  }

  if (!current) {
    return (
      <div className="cr-modal-overlay" onClick={onClose}>
        <div className="cr-modal" onClick={e => e.stopPropagation()}>
          <div className="cr-modal-header">
            <h2>Vista previa</h2>
            <button className="cr-modal-close" onClick={onClose}><X size={18} /></button>
          </div>
          <div className="cr-modal-body">
            <p style={{ color: 'var(--text-muted)' }}>No hay clientes del Círculo Rojo todavía.</p>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="cr-modal-overlay" onClick={onClose}>
      <div className="cr-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 1100 }}>
        <div className="cr-modal-header">
          <h2>Vista previa · Credencial Círculo Rojo</h2>
          <button className="cr-modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="cr-modal-body">
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)' }}>Cliente:</label>
            <select
              value={current.id}
              onChange={e => onSelect(e.target.value)}
              style={{
                padding: '8px 12px',
                borderRadius: 8,
                border: '1px solid var(--border)',
                background: 'var(--input-bg, var(--card))',
                color: 'var(--text-main)',
                fontSize: 14,
                minWidth: 280,
              }}
            >
              {customers.map(c => (
                <option key={c.id} value={c.id}>
                  {c.name} {c.coupon_code ? `· ${c.coupon_code}` : '(sin cupón)'} {c.source_origin === 'epic_week_2026' ? '🟠' : ''}
                </option>
              ))}
            </select>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Segmento: {current.source_origin || '—'}
            </span>
          </div>

          {/* Captura: card a tamaño real, fuera de pantalla. Por encima rendereamos un scale 0.6 visible. */}
          <div style={{ position: 'absolute', left: -99999, top: 0 }}>
            <CirculoRojoCard
              ref={cardRef}
              name={current.name}
              couponCode={current.coupon_code}
              firstOrderAt={current.first_order_at}
            />
          </div>
          <div className="crc-frame">
            <div className="crc-scaled" style={{ transform: 'scale(0.6)', height: 630 * 0.6, width: 1080 * 0.6 }}>
              <CirculoRojoCard
                name={current.name}
                couponCode={current.coupon_code}
                firstOrderAt={current.first_order_at}
              />
            </div>
          </div>

          {/* Bloque de test send */}
          <div style={{
            marginTop: 18,
            padding: 16,
            border: '1px solid var(--border)',
            borderRadius: 12,
            background: 'var(--bg-secondary, rgba(0,0,0,0.03))',
          }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)' }}>Template:</label>
              <select
                value={templateName}
                onChange={e => setTemplateName(e.target.value)}
                style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--input-bg, var(--card))', color: 'var(--text-main)', fontSize: 13 }}
              >
                <option value="circulo_rojo_credencial_existente">circulo_rojo_credencial_existente (históricos)</option>
                <option value="circulo_rojo_credencial_nuevo">circulo_rojo_credencial_nuevo (Epic Week)</option>
                <option value="circulo_rojo_credencial_amigo">circulo_rojo_credencial_amigo (Día del Amigo)</option>
              </select>
              <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginLeft: 10 }}>Phone test:</label>
              <input
                type="text"
                value={testPhone}
                onChange={e => setTestPhone(e.target.value)}
                placeholder="5491169965506"
                style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--input-bg, var(--card))', color: 'var(--text-main)', fontSize: 13, width: 160, fontFamily: 'monospace' }}
              />
              <button
                onClick={handleSendTest}
                disabled={sending}
                className="cr-btn cr-btn-primary"
                style={{ marginLeft: 'auto' }}
              >
                {sending ? 'Enviando…' : 'Mandar test'}
              </button>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Captura el card de arriba con html2canvas → sube a Meta como media → manda template aprobado con header de imagen + nombre y cupón en el body.
              Requiere que los templates estén <strong>APPROVED</strong> en Meta (hoy están PENDING).
            </div>
            {sendResult && (
              <div style={{
                marginTop: 10,
                padding: '8px 12px',
                borderRadius: 6,
                background: sendResult.ok ? 'rgba(16, 185, 129, 0.12)' : 'rgba(220, 38, 38, 0.12)',
                color: sendResult.ok ? '#059669' : '#dc2626',
                fontSize: 12,
                fontFamily: 'monospace',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
              }}>
                {sendResult.msg}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

interface ModalProps {
  customer: Customer;
  saving: boolean;
  isNew?: boolean;
  onChange: (c: Customer) => void;
  onSave: (c: Customer) => void;
  onDelete?: () => void;
  onClose: () => void;
}

function CustomerModal({ customer, saving, isNew, onChange, onSave, onDelete, onClose }: ModalProps) {
  const [local, setLocal] = useState<Customer>(customer);
  useEffect(() => setLocal(customer), [customer.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (patch: Partial<Customer>) => {
    const next = { ...local, ...patch };
    setLocal(next);
    if (!isNew) onChange(next);
  };

  return (
    <div className="cr-modal-overlay" onClick={onClose}>
      <div className="cr-modal" onClick={e => e.stopPropagation()}>
        <div className="cr-modal-header">
          <h2>{isNew ? 'Nuevo cliente' : 'Editar cliente'}</h2>
          <button className="cr-modal-close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>
        <div className="cr-modal-body">
          <label className="cr-field">
            <span>Nombre</span>
            <input type="text" value={local.name} onChange={e => update({ name: e.target.value })} />
          </label>
          <label className="cr-field">
            <span>Teléfono / WhatsApp</span>
            <input
              type="text"
              value={local.phone_display || ''}
              onChange={e => update({ phone_display: e.target.value })}
              placeholder="+54 9 11 1234-5678"
            />
          </label>
          <label className="cr-field">
            <span>Email</span>
            <input type="email" value={local.email || ''} onChange={e => update({ email: e.target.value })} />
          </label>
          <label className="cr-field">
            <span>Notas</span>
            <textarea
              rows={3}
              value={local.notes || ''}
              onChange={e => update({ notes: e.target.value })}
              placeholder="Notas internas: cómo se conocieron, qué prefiere, descuento acordado, etc."
            />
          </label>
          <label className="cr-checkbox">
            <input
              type="checkbox"
              checked={local.circulo_rojo}
              onChange={e => update({ circulo_rojo: e.target.checked })}
            />
            <Circle size={16} fill={local.circulo_rojo ? '#dc2626' : 'none'} color="#dc2626" />
            Miembro del Círculo Rojo
          </label>
          {!isNew && (
            <div className="cr-meta">
              <div>Pedidos: <b>{local.order_count}</b></div>
              <div>Total: <b>${fmtARS(local.total_spent || 0)}</b></div>
              <div>Primer pedido: <b>{fmtDate(local.first_order_at)}</b></div>
              <div>Último pedido: <b>{fmtDate(local.last_order_at)}</b></div>
              {local.source_origin && <div>Origen: <b>{local.source_origin}</b></div>}
            </div>
          )}
        </div>
        <div className="cr-modal-footer">
          {!isNew && onDelete && (
            <button className="cr-btn cr-btn-danger" onClick={onDelete} disabled={saving}>
              <Trash2 size={16} /> Eliminar
            </button>
          )}
          <div style={{ flex: 1 }} />
          <button className="cr-btn cr-btn-secondary" onClick={onClose}>Cancelar</button>
          <button className="cr-btn cr-btn-primary" onClick={() => onSave(local)} disabled={saving}>
            <Save size={16} /> {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface ImportModalProps {
  saving: boolean;
  onClose: () => void;
  onImport: (rows: ParsedRow[]) => void;
}

function ImportModal({ saving, onClose, onImport }: ImportModalProps) {
  const [text, setText] = useState('');
  const parsed = useMemo(() => parseImportText(text), [text]);

  return (
    <div className="cr-modal-overlay" onClick={onClose}>
      <div className="cr-modal cr-modal-wide" onClick={e => e.stopPropagation()}>
        <div className="cr-modal-header">
          <h2>Importar lista de Círculo Rojo</h2>
          <button className="cr-modal-close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>
        <div className="cr-modal-body">
          <p className="cr-help">
            Pegá una lista de clientes — una línea por cliente. El parser intenta detectar nombre, teléfono y
            email automáticamente. Formato libre: separadores tab, coma o espacio.
          </p>
          <textarea
            className="cr-import-textarea"
            rows={12}
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder={`Maria Lopez    +54 9 11 1234-5678    maria@example.com\nJuan Perez,1166778899\nstephanie@krilic.com.ar Stephanie`}
          />
          <div className="cr-import-preview">
            <h3>Preview ({parsed.length})</h3>
            <table className="cr-table cr-table-mini">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Teléfono</th>
                  <th>Email</th>
                </tr>
              </thead>
              <tbody>
                {parsed.slice(0, 50).map((r, i) => (
                  <tr key={i}>
                    <td>{r.name}</td>
                    <td>{r.phone_display || <span className="cr-dim">—</span>}</td>
                    <td>{r.email || <span className="cr-dim">—</span>}</td>
                  </tr>
                ))}
                {parsed.length > 50 && (
                  <tr>
                    <td colSpan={3} className="cr-dim">…{parsed.length - 50} más</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="cr-modal-footer">
          <div style={{ flex: 1 }} />
          <button className="cr-btn cr-btn-secondary" onClick={onClose}>Cancelar</button>
          <button
            className="cr-btn cr-btn-primary"
            onClick={() => onImport(parsed)}
            disabled={saving || parsed.length === 0}
          >
            <Upload size={16} /> {saving ? 'Importando…' : `Importar ${parsed.length}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CirculoRojo;
